import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Button,
    TextField,
    MenuItem,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    IconButton,
    Chip,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import { API_URL } from '../../api';
import EmailChipInput from '../../components/EmailChipInput';
import EmailAttachmentsInput from '../../components/EmailAttachmentsInput';
import HowItWorksCard from '../../components/HowItWorksCard';
import { useConfirm } from '../../components/ConfirmDialog';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const TERM_NAMES = ['Term 1', 'Term 2', 'Term 3'];
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');

const WEEKDAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const dateKey = (d) => {
    const dt = d instanceof Date ? d : new Date(d);
    const y = dt.getUTCFullYear();
    const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
    const day = String(dt.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const humanDate = (d, withWeekday = false) =>
    new Date(d).toLocaleDateString('en-GB', {
        weekday: withWeekday ? 'long' : undefined,
        day: 'numeric',
        month: 'long',
        timeZone: 'UTC',
    });

const humanDateShort = (d) =>
    new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });

/**
 * Expand `[ { date, endDate?, reason } ]` rows into a flat list with one
 * entry per day. Inclusive of endDate. Rows without `endDate` pass
 * through as a single-day entry.
 */
function expandRanges(rows) {
    const out = [];
    for (const r of rows) {
        if (!r?.date) continue;
        const from = new Date(r.date);
        const to = r.endDate ? new Date(r.endDate) : from;
        if (isNaN(from.getTime()) || isNaN(to.getTime()) || to < from) {
            out.push({ date: r.date, reason: r.reason || '' });
            continue;
        }
        const cursor = new Date(from);
        while (cursor <= to) {
            out.push({ date: dateKey(cursor), reason: r.reason || '' });
            cursor.setUTCDate(cursor.getUTCDate() + 1);
        }
    }
    return out;
}

/**
 * Re-collapse server data (flat per-day rows) back into ranges so the
 * editor shows half-term as one row, not five. Groups consecutive days
 * that share a reason.
 */
function collapseToRanges(rows) {
    const sorted = rows
        .filter((r) => r?.date)
        .map((r) => ({ date: String(r.date).slice(0, 10), reason: r.reason || '' }))
        .sort((a, b) => a.date.localeCompare(b.date));

    const result = [];
    for (const r of sorted) {
        const last = result[result.length - 1];
        if (last) {
            const nextDay = new Date(last.endDate || last.date);
            nextDay.setUTCDate(nextDay.getUTCDate() + 1);
            const nextKey = dateKey(nextDay);
            if (r.date === nextKey && r.reason === last.reason) {
                last.endDate = r.date;
                continue;
            }
        }
        result.push({ date: r.date, endDate: '', reason: r.reason });
    }
    return result;
}

export default function TermsPage() {
    const confirm = useConfirm();
    const { yearId } = useParams();
    const [year, setYear] = useState(null);
    const [terms, setTerms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const [termDialogOpen, setTermDialogOpen] = useState(false);
    const [editingTerm, setEditingTerm] = useState(null);
    const [termName, setTermName] = useState('Term 1');
    const [termStartsOn, setTermStartsOn] = useState('');
    const [termEndsOn, setTermEndsOn] = useState('');
    const [blockedDates, setBlockedDates] = useState([]);
    const [saving, setSaving] = useState(false);

    const [broadcastOpen, setBroadcastOpen] = useState(false);
    const [broadcastTermId, setBroadcastTermId] = useState('');
    const [broadcastRoles, setBroadcastRoles] = useState({ parent: true, tutor: true, student: true });
    const [broadcastExtraEmails, setBroadcastExtraEmails] = useState([]);
    const [broadcastSending, setBroadcastSending] = useState(false);
    const [broadcastPreview, setBroadcastPreview] = useState(null); // { subject, html, recipientCount }
    const [broadcastPreviewLoading, setBroadcastPreviewLoading] = useState(false);
    const [broadcastAttachments, setBroadcastAttachments] = useState([]);

    const [calendarTerm, setCalendarTerm] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API_URL}/api/admin/academic-years/${yearId}`, authHeaders());
            setYear(data.year);
            setTerms(data.terms || []);
        } catch {
            setError('Failed to load.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [yearId]);

    const openCreate = () => {
        setEditingTerm(null);
        const taken = new Set(terms.map((t) => t.name));
        const next = TERM_NAMES.find((n) => !taken.has(n)) || 'Term 1';
        setTermName(next);
        setTermStartsOn(year?.startsOn ? year.startsOn.slice(0, 10) : '');
        setTermEndsOn(year?.endsOn ? year.endsOn.slice(0, 10) : '');
        setBlockedDates([]);
        setTermDialogOpen(true);
    };

    const openEdit = (t) => {
        setEditingTerm(t);
        setTermName(t.name);
        setTermStartsOn(t.startsOn ? t.startsOn.slice(0, 10) : '');
        setTermEndsOn(t.endsOn ? t.endsOn.slice(0, 10) : '');
        // Collapse consecutive same-reason dates back into ranges so the
        // editor shows them as a single row.
        setBlockedDates(collapseToRanges(t.blockedDates || []));
        setTermDialogOpen(true);
    };

    const closeTermDialog = () => setTermDialogOpen(false);

    // Range-aware row shape: { date, endDate, reason }. endDate empty = single day.
    const addBlockedRow = () =>
        setBlockedDates((arr) => [...arr, { date: '', endDate: '', reason: '' }]);
    const updateBlockedRow = (idx, key, val) =>
        setBlockedDates((arr) => arr.map((b, i) => (i === idx ? { ...b, [key]: val } : b)));
    const removeBlockedRow = (idx) =>
        setBlockedDates((arr) => arr.filter((_, i) => i !== idx));

    const saveTerm = async () => {
        if (!termStartsOn || !termEndsOn) {
            setError('Start and end are required.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            const payload = {
                name: termName,
                startsOn: termStartsOn,
                endsOn: termEndsOn,
                // Expand any (date, endDate) range into one row per day so
                // the backend can keep its single-date schema.
                blockedDates: expandRanges(blockedDates),
            };
            if (editingTerm) {
                await axios.patch(
                    `${API_URL}/api/admin/terms/${editingTerm._id}`,
                    payload,
                    authHeaders()
                );
                setMessage('Term updated.');
            } else {
                await axios.post(
                    `${API_URL}/api/admin/terms`,
                    { ...payload, academicYearId: yearId },
                    authHeaders()
                );
                setMessage('Term created.');
            }
            closeTermDialog();
            await load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setSaving(false);
        }
    };

    const removeTerm = async (id) => {
        if (!(await confirm({ title: 'Delete term', message: 'Delete this term? Related lessons stay in place but lose the term label.', confirmLabel: 'Delete', danger: true }))) return;
        try {
            await axios.delete(`${API_URL}/api/admin/terms/${id}`, authHeaders());
            setMessage('Term deleted.');
            await load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        }
    };

    const openBroadcast = (id) => {
        setBroadcastTermId(id);
        setBroadcastRoles({ parent: true, tutor: true, student: true });
        setBroadcastExtraEmails([]);
        setBroadcastPreview(null);
        setBroadcastOpen(true);
    };

    // Live preview — refetches when the dialog opens or when roles / extras change.
    useEffect(() => {
        if (!broadcastOpen || !broadcastTermId) return;
        const roles = Object.entries(broadcastRoles).filter(([, v]) => v).map(([k]) => k);
        const params = new URLSearchParams();
        if (roles.length) params.set('roles', roles.join(','));
        if (broadcastExtraEmails.length) params.set('extraEmails', broadcastExtraEmails.join(','));
        setBroadcastPreviewLoading(true);
        axios
            .get(
                `${API_URL}/api/admin/terms/${broadcastTermId}/broadcast-preview?${params.toString()}`,
                authHeaders()
            )
            .then((r) => setBroadcastPreview(r.data || null))
            .catch(() => setBroadcastPreview(null))
            .finally(() => setBroadcastPreviewLoading(false));
    }, [broadcastOpen, broadcastTermId, broadcastRoles, broadcastExtraEmails]);

    const sendBroadcast = async () => {
        const roles = Object.entries(broadcastRoles)
            .filter(([, v]) => v)
            .map(([k]) => k);
        if (!roles.length && broadcastExtraEmails.length === 0) {
            setError('Pick at least one audience or add an extra email.');
            return;
        }
        setBroadcastSending(true);
        setError('');
        try {
            const { data } = await axios.post(
                `${API_URL}/api/admin/terms/${broadcastTermId}/broadcast`,
                { roles, extraEmails: broadcastExtraEmails, attachments: broadcastAttachments },
                authHeaders()
            );
            setMessage(`Broadcast sent to ${data.sent} of ${data.requested} recipients.`);
            setBroadcastOpen(false);
            setBroadcastAttachments([]);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBroadcastSending(false);
        }
    };

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4">{year?.label || 'Terms'}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 760 }}>
                        Set the start and end date for each term. You can also add blocked dates,
                        such as half-term, bank holidays or Easter. Blocked dates will be skipped
                        automatically when recurring lessons are generated.
                    </Typography>
                    
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button component={RouterLink} to="/academic-years" variant="outlined">Back</Button>
                    <Button onClick={openCreate} variant="contained" sx={{ color: '#fff !important' }}>
                        Add term
                    </Button>
                </Stack>
            </Stack>
            <Box sx={{ mb: 3 }}>
                <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>1. Add each term</strong> with its start and end date. Only the
                            three fixed names are accepted — you can't create a fourth.<br />
                            <strong>2. Add blocked dates</strong> as a single day or a range. Each
                            row carries an optional reason (e.g. <em>Half-term</em>, <em>Easter</em>,
                            <em>Bank holiday</em>) which appears on the broadcast email.<br />
                            <strong>3. When a tutoring class is published</strong>, lesson rows are
                            generated for every date in every term the class belongs to — <strong>blocked
                            dates are skipped automatically</strong>. If you add a blocked date later,
                            regenerate lessons from the class detail page so the register stays in sync.<br />
                            <strong>4. Broadcast term dates</strong> emails the full calendar (term
                            start / end + blocked dates) to parents, tutors, students, or any extra
                            addresses. The email uses the template defined in <em>Settings →
                            Operational emails → Term dates broadcast</em>.<br />
                            <strong>5. Deleting a term</strong> is safe if no class has been published
                            against it. Deleting a term with published classes will orphan the
                            generated lesson rows — check the class detail page first.
                        </Typography>
                    </HowItWorksCard>
            </Box>


            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

            <Card>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Term</TableCell>
                                <TableCell>Starts</TableCell>
                                <TableCell>Ends</TableCell>
                                <TableCell>Blocked dates</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {terms.map((t) => (
                                <TableRow key={t._id}>
                                    <TableCell>{t.name}</TableCell>
                                    <TableCell>{fmtDate(t.startsOn)}</TableCell>
                                    <TableCell>{fmtDate(t.endsOn)}</TableCell>
                                    <TableCell>
                                        {t.blockedDates?.length
                                            ? <Chip size="small" label={`${t.blockedDates.length} blocked`} />
                                            : '—'}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button size="small" onClick={() => setCalendarTerm(t)}>View calendar</Button>
                                        <IconButton onClick={() => openBroadcast(t._id)} title="Send term dates to everyone">
                                            <SendIcon />
                                        </IconButton>
                                        <IconButton onClick={() => openEdit(t)}><EditIcon /></IconButton>
                                        <IconButton onClick={() => removeTerm(t._id)}><DeleteIcon /></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {terms.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                            No terms yet — click "Add term".
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </Card>

            {/* Term create/edit dialog */}
            <Dialog open={termDialogOpen} onClose={closeTermDialog} maxWidth="md" fullWidth>
                <DialogTitle>{editingTerm ? `Edit ${editingTerm.name}` : 'Add term'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            select
                            label="Term"
                            value={termName}
                            onChange={(e) => setTermName(e.target.value)}
                            size="small"
                            disabled={!!editingTerm}
                        >
                            {TERM_NAMES.map((n) => (
                                <MenuItem key={n} value={n}>{n}</MenuItem>
                            ))}
                        </TextField>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Starts on"
                                type="date"
                                value={termStartsOn}
                                onChange={(e) => setTermStartsOn(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                label="Ends on"
                                type="date"
                                value={termEndsOn}
                                onChange={(e) => setTermEndsOn(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                sx={{ flex: 1 }}
                            />
                        </Stack>
                        <Box>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                <Typography variant="subtitle2">Blocked dates</Typography>
                                <Button onClick={addBlockedRow} startIcon={<AddIcon />} size="small">
                                    Add dates to block
                                </Button>
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Blocked dates are dates when lessons should not be scheduled, such as
                                half-term, bank holidays or Easter.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                <strong>For one day:</strong> enter the same date in <strong>From</strong> and <strong>To</strong>.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                <strong>For several days:</strong> enter the start date in <strong>From</strong> and the last
                                blocked date in <strong>To</strong>.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Example:<br />
                                <strong>From:</strong> 26/10/2026<br />
                                <strong>To:</strong> 30/10/2026
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                This blocks all dates from <strong>26 October to 30 October</strong>, including both dates.
                            </Typography>
                            <Stack spacing={1}>
                                {blockedDates.map((b, idx) => (
                                    <Stack direction="row" spacing={1} key={idx} alignItems="center">
                                        <TextField
                                            type="date"
                                            label="From"
                                            value={b.date}
                                            onChange={(e) => updateBlockedRow(idx, 'date', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            size="small"
                                            sx={{ width: 160 }}
                                        />
                                        <TextField
                                            type="date"
                                            label="To (optional)"
                                            value={b.endDate || ''}
                                            onChange={(e) => updateBlockedRow(idx, 'endDate', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            size="small"
                                            sx={{ width: 170 }}
                                        />
                                        <TextField
                                            label="Reason"
                                            placeholder="e.g. Half-term"
                                            value={b.reason}
                                            onChange={(e) => updateBlockedRow(idx, 'reason', e.target.value)}
                                            size="small"
                                            sx={{ flex: 1 }}
                                        />
                                        <IconButton onClick={() => removeBlockedRow(idx)}><DeleteIcon /></IconButton>
                                    </Stack>
                                ))}
                                {blockedDates.length === 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                        No blocked dates — every weekday in range is teachable.
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeTermDialog} disabled={saving}>Cancel</Button>
                    <Button onClick={saveTerm} variant="contained" disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Broadcast dialog */}
            <Dialog open={broadcastOpen} onClose={() => setBroadcastOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>Send term dates</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
                        {/* Left side — recipients */}
                        <Box sx={{ flex: '0 0 320px' }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Audiences</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Sends the calendar email to every active account in the selected
                                groups, plus any extra emails you add below.
                            </Typography>
                            <Stack spacing={1} sx={{ mb: 2 }}>
                                {['parent', 'tutor', 'student'].map((role) => (
                                    <Stack key={role} direction="row" alignItems="center" spacing={1}>
                                        <input
                                            type="checkbox"
                                            checked={!!broadcastRoles[role]}
                                            onChange={(e) =>
                                                setBroadcastRoles((r) => ({ ...r, [role]: e.target.checked }))
                                            }
                                        />
                                        <Typography textTransform="capitalize">{role}s</Typography>
                                    </Stack>
                                ))}
                            </Stack>

                            <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Also send to</Typography>
                            <EmailChipInput
                                value={broadcastExtraEmails}
                                onChange={setBroadcastExtraEmails}
                                label="Extra email"
                                helperText="Press Enter to add. Useful for non-registered recipients."
                                addLabel="Add"
                            />

                            <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Attachments</Typography>
                            <EmailAttachmentsInput
                                value={broadcastAttachments}
                                onChange={setBroadcastAttachments}
                                onError={setError}
                                folder="term-broadcasts"
                                helpText="Optional. Each file ≤ 25 MB. Sent to every recipient."
                            />

                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Will send to <strong>{broadcastPreview?.recipientCount ?? '…'}</strong> recipient(s).
                                </Typography>
                            </Box>
                        </Box>

                        {/* Right side — live preview */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Email preview
                            </Typography>
                            {broadcastPreview && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    <strong>Subject:</strong> {broadcastPreview.subject}
                                </Typography>
                            )}
                            <Box
                                sx={{
                                    border: 1, borderColor: 'divider', borderRadius: 1, p: 2,
                                    maxHeight: 480, overflowY: 'auto', bgcolor: '#fff',
                                    fontSize: 13,
                                }}
                            >
                                {broadcastPreviewLoading && !broadcastPreview && (
                                    <Typography color="text.secondary">Loading preview…</Typography>
                                )}
                                {broadcastPreview && (
                                    <div dangerouslySetInnerHTML={{ __html: broadcastPreview.html }} />
                                )}
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBroadcastOpen(false)} disabled={broadcastSending}>Cancel</Button>
                    <Button onClick={sendBroadcast} variant="contained" disabled={broadcastSending}>
                        {broadcastSending ? 'Sending…' : 'Send'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Calendar view dialog */}
            <Dialog open={!!calendarTerm} onClose={() => setCalendarTerm(null)} maxWidth="xl" fullWidth>
                <DialogTitle>
                    {calendarTerm
                        ? `${calendarTerm.name}: ${humanDateMonth(calendarTerm.startsOn)} – ${humanDateMonth(calendarTerm.endsOn)}`
                        : 'Calendar'}
                </DialogTitle>
                <DialogContent dividers>
                    {calendarTerm && <TermCalendarView term={calendarTerm} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCalendarTerm(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

const humanDateMonth = (d) =>
    new Date(d).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/**
 * Spreadsheet-style calendar matching the broadcast email layout: one
 * column per weekday with active lessons, numbered lists of dates, plus
 * highlighted "block" rows where every weekday in that week is blocked
 * (e.g. half-term). Easter / bank-holiday singles inside otherwise-normal
 * weeks are inlined with a yellow background.
 */
function TermCalendarView({ term }) {
    // Two-view toggle:
    //   'dates'   → calendar of teaching dates (potential Mondays, etc.)
    //   'classes' → the actual tutoring classes SCHEDULED to run in this
    //               term, grouped by weekday, with tutor + roster size.
    // Fetches lazily — nothing hits the server until the admin flips to
    // the classes view.
    const [viewMode, setViewMode] = useState('dates');
    const [scheduledClasses, setScheduledClasses] = useState(null);
    const [classesLoading, setClassesLoading] = useState(false);
    const [classesError, setClassesError] = useState('');

    useEffect(() => {
        if (viewMode !== 'classes' || scheduledClasses !== null) return;
        setClassesLoading(true);
        setClassesError('');
        axios
            .get(`${API_URL}/api/admin/tutoring-classes?termId=${term._id}`, authHeaders())
            .then((r) => setScheduledClasses(Array.isArray(r.data) ? r.data : []))
            .catch((e) => setClassesError(e?.response?.data?.message || 'Failed to load.'))
            .finally(() => setClassesLoading(false));
    }, [viewMode, term._id, scheduledClasses]);

    const startsOn = new Date(term.startsOn);
    const endsOn = new Date(term.endsOn);

    const blockedMap = new Map(
        (term.blockedDates || []).map((b) => [dateKey(new Date(b.date)), b.reason || ''])
    );

    // Walk weeks (Monday-anchored). For each week, build a per-weekday map.
    const weeks = [];
    const cursor = new Date(startsOn);
    // Snap back to Monday of starting week.
    while (cursor.getUTCDay() !== 1) cursor.setUTCDate(cursor.getUTCDate() - 1);
    while (cursor <= endsOn) {
        const week = { weekStart: new Date(cursor), days: {} };
        for (let i = 0; i < 7; i++) {
            const d = new Date(cursor);
            d.setUTCDate(d.getUTCDate() + i);
            if (d < startsOn || d > endsOn) continue;
            const k = dateKey(d);
            const reason = blockedMap.has(k) ? blockedMap.get(k) : null;
            week.days[d.getUTCDay()] = { date: d, blocked: reason !== null, reason };
        }
        weeks.push(week);
        cursor.setUTCDate(cursor.getUTCDate() + 7);
    }

    // Which weekdays should be columns? Any with at least one non-blocked
    // date across the whole term.
    const dayOrder = [1, 2, 3, 4, 5, 6, 0]; // Mon → Sun
    const activeDays = dayOrder.filter((dow) =>
        weeks.some((w) => w.days[dow] && !w.days[dow].blocked)
    );

    // Group consecutive "all-blocked" weeks (e.g. half-term week) into
    // banded segments. A week counts as block-only when every active column
    // day in that week is blocked.
    const segments = [];
    let normalRun = [];
    const flushNormal = () => {
        if (normalRun.length) {
            segments.push({ kind: 'normal', weeks: normalRun });
            normalRun = [];
        }
    };
    for (const w of weeks) {
        const present = activeDays.map((d) => w.days[d]).filter(Boolean);
        if (present.length > 0 && present.every((x) => x.blocked)) {
            flushNormal();
            segments.push({ kind: 'block', week: w });
        } else {
            normalRun.push(w);
        }
    }
    flushNormal();

    if (!activeDays.length) {
        return <Typography color="text.secondary">No teaching dates in this term.</Typography>;
    }

    // Per-weekday class count (non-blocked dates across the whole term)
    // and a running total for the header pill. Client asked for these
    // to make "how many classes in Term 1" visible at a glance.
    const perDayCount = Object.fromEntries(
        activeDays.map((dow) => [
            dow,
            weeks.reduce((n, w) => n + (w.days[dow] && !w.days[dow].blocked ? 1 : 0), 0),
        ])
    );
    const totalClasses = Object.values(perDayCount).reduce((a, b) => a + b, 0);

    const headerBg = '#d6ebff';

    // Group scheduled classes by weekday for the alternate view.
    const scheduledByDay = {};
    if (scheduledClasses) {
        for (const dow of activeDays) scheduledByDay[dow] = [];
        for (const c of scheduledClasses) {
            const dow = c.dayOfWeek;
            if (scheduledByDay[dow]) scheduledByDay[dow].push(c);
            else if (dow !== undefined && dow !== null) scheduledByDay[dow] = [c];
        }
    }
    const scheduledTotal = scheduledClasses ? scheduledClasses.length : 0;

    // Pre-compute the non-blocked date list per weekday — reused by the
    // "Scheduled classes" view so each class row can show the actual
    // dates it runs on across the whole term.
    const datesByDay = Object.fromEntries(
        activeDays.map((dow) => [
            dow,
            weeks
                .map((w) => w.days[dow])
                .filter((d) => d && !d.blocked)
                .map((d) => d.date),
        ])
    );

    // Pill label swaps to reflect the current view.
    const pillLabel =
        viewMode === 'dates'
            ? `${term.name}: ${humanDateMonth(term.startsOn)} – ${humanDateMonth(term.endsOn)} · ${totalClasses} teaching ${totalClasses === 1 ? 'date' : 'dates'}`
            : `${term.name}: ${humanDateMonth(term.startsOn)} – ${humanDateMonth(term.endsOn)} · ${scheduledTotal} tutoring ${scheduledTotal === 1 ? 'class' : 'classes'} scheduled`;

    return (
        <Box sx={{ overflowX: 'auto' }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
                <Chip
                    label={pillLabel}
                    sx={{ bgcolor: '#6D3FC5', color: '#fff', fontWeight: 600 }}
                />
                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    size="small"
                    onChange={(_, v) => { if (v) setViewMode(v); }}
                >
                    <ToggleButton value="dates">Teaching dates</ToggleButton>
                    <ToggleButton value="classes">Scheduled classes</ToggleButton>
                </ToggleButtonGroup>
            </Stack>
            {viewMode === 'classes' && (
                <ScheduledClassesView
                    activeDays={activeDays}
                    scheduledByDay={scheduledByDay}
                    datesByDay={datesByDay}
                    loading={classesLoading}
                    error={classesError}
                />
            )}
            {viewMode === 'dates' && (
            <>
            <Box
                component="table"
                sx={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontFamily: 'Arial, sans-serif',
                    fontSize: 13,
                    '& th, & td': { border: '1px solid #cbd6e0', padding: '6px 10px', verticalAlign: 'top' },
                }}
            >
                <thead>
                    <tr style={{ background: headerBg }}>
                        {activeDays.map((dow) => (
                            <th key={dow} style={{ textAlign: 'left', fontWeight: 700 }}>
                                {WEEKDAYS_LONG[dow]} Classes ({perDayCount[dow]})
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {segments.map((seg, i) => {
                        if (seg.kind === 'block') {
                            const colSpan = activeDays.length;
                            const reason = activeDays
                                .map((d) => seg.week.days[d]?.reason)
                                .find(Boolean) || 'Break';
                            const samples = activeDays
                                .map((d) => seg.week.days[d])
                                .filter(Boolean);
                            const first = samples[0]?.date;
                            const last = samples[samples.length - 1]?.date;
                            return (
                                <tr key={`b${i}`}>
                                    <td colSpan={colSpan} style={{ background: '#fff4cc', fontWeight: 600 }}>
                                        {humanDate(first)}{first && last && humanDate(first) !== humanDate(last) ? ` – ${humanDate(last)}` : ''}
                                        {' — '}{reason}
                                    </td>
                                </tr>
                            );
                        }
                        // Normal segment: render numbered list per column.
                        // Numbering only counts non-blocked (actual class)
                        // dates — blocked days are still shown so parents
                        // see WHY a date is skipped, but they don't consume
                        // a class number (client's ask).
                        const perColumn = activeDays.map((dow) => {
                            let n = 0;
                            return seg.weeks
                                .map((w) => w.days[dow])
                                .filter(Boolean)
                                .map((d) => {
                                    const seq = d.blocked ? null : ++n;
                                    return { ...d, _key: dateKey(d.date), seq };
                                });
                        });
                        const maxRows = Math.max(...perColumn.map((c) => c.length), 0);
                        const rows = [];
                        for (let r = 0; r < maxRows; r++) {
                            rows.push(
                                <tr key={`n${i}-${r}`}>
                                    {activeDays.map((dow, ci) => {
                                        const cell = perColumn[ci][r];
                                        if (!cell) return <td key={dow} />;
                                        const prefix = cell.seq != null ? `${cell.seq}. ` : '';
                                        const dateStr = `${WEEKDAYS_LONG[dow].slice(0, 3)} ${humanDateShort(cell.date)}`;
                                        return (
                                            <td
                                                key={dow}
                                                style={cell.blocked ? { background: '#fff4cc' } : {}}
                                            >
                                                {cell.blocked ? (
                                                    <>
                                                        <span style={{ textDecoration: 'line-through', color: '#a66f00' }}>
                                                            {dateStr}
                                                        </span>
                                                        {cell.reason ? (
                                                            <span style={{ color: '#a66f00', marginLeft: 6 }}>
                                                                — {cell.reason}
                                                            </span>
                                                        ) : null}
                                                    </>
                                                ) : (
                                                    `${prefix}${dateStr}`
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        }
                        return rows;
                    })}
                </tbody>
            </Box>

            {/* Footer: blocked dates listing */}
            {(term.blockedDates || []).length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">No lessons on:</Typography>
                    <Box component="ul" sx={{ m: 0, pl: 3, fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
                        {[...(term.blockedDates || [])]
                            .sort((a, b) => new Date(a.date) - new Date(b.date))
                            .map((b) => (
                                <li key={dateKey(new Date(b.date)) + b.reason}>
                                    {humanDate(b.date, true)}
                                    {b.reason ? ` — ${b.reason}` : ''}
                                </li>
                            ))}
                    </Box>
                </Box>
            )}
            </>
            )}
        </Box>
    );
}

/**
 * Alternate view for the term calendar: shows every tutoring class
 * scheduled for this term with the FULL list of dates each class runs.
 * Header row = the weekday the class meets on, then each class is a
 * row listing its dates (numbered, matching what the parent will see
 * on the class register).
 *
 * Fed by a lazy `GET /api/admin/tutoring-classes?termId=…` — nothing
 * hits the server until the admin flips the toggle.
 */
function ScheduledClassesView({ activeDays, scheduledByDay, datesByDay, loading, error }) {
    if (loading) {
        return (
            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
            </Box>
        );
    }
    if (error) {
        return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
    }
    const total = activeDays.reduce((n, d) => n + (scheduledByDay[d]?.length || 0), 0);
    if (total === 0) {
        return (
            <Alert severity="info" sx={{ my: 2 }}>
                No tutoring classes are currently scheduled for this term. Create classes under
                <strong> Teaching → Tutoring classes</strong> and tick this term in the class detail.
            </Alert>
        );
    }

    // Flatten scheduled classes in weekday order for a clean per-class listing.
    const orderedClasses = activeDays.flatMap((dow) =>
        (scheduledByDay[dow] || []).map((c) => ({ ...c, __dow: dow }))
    );

    return (
        <Box>
            {orderedClasses.map((c) => {
                const dates = datesByDay[c.__dow] || [];
                const tutor = c.tutorId
                    ? `${c.tutorId.firstName || ''} ${c.tutorId.surname || ''}`.trim()
                    : '(no tutor assigned)';
                const roster = c.studentIds?.length || 0;
                return (
                    <Box
                        key={c._id}
                        sx={{
                            mb: 2,
                            border: '1px solid #cbd6e0',
                            borderRadius: 1,
                            overflow: 'hidden',
                            fontFamily: 'Arial, sans-serif',
                            fontSize: 13,
                        }}
                    >
                        {/* Class header — name / tutor / roster / weekday */}
                        <Box sx={{ p: 1.5, bgcolor: '#EFE7FB' }}>
                            <RouterLink
                                to={`/tutoring-classes/${c._id}`}
                                style={{ fontWeight: 700, color: '#6D3FC5', fontSize: 14 }}
                            >
                                {c.name}
                            </RouterLink>
                            <Box sx={{ color: '#475467', mt: 0.25 }}>
                                {WEEKDAYS_LONG[c.__dow]}s
                                {c.startsAt ? ` · ${c.startsAt}` : ''}
                                {c.durationMinutes ? ` · ${c.durationMinutes} min` : ''}
                                {' · '}{tutor}
                                {' · '}{roster} student{roster === 1 ? '' : 's'}
                                {' · '}<strong>{dates.length} {dates.length === 1 ? 'session' : 'sessions'}</strong>
                            </Box>
                        </Box>

                        {/* Per-session grid — numbered dates flow left-to-right,
                            wrap on narrow screens. Matches what the parent sees
                            in the term-dates broadcast email for this weekday. */}
                        <Box sx={{ p: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {dates.length === 0 ? (
                                <span style={{ color: '#888' }}>No teaching dates in this term.</span>
                            ) : (
                                dates.map((d, i) => (
                                    <Box
                                        key={dateKey(d)}
                                        sx={{
                                            px: 1,
                                            py: 0.5,
                                            bgcolor: '#F3F4F6',
                                            borderRadius: 0.5,
                                            fontVariantNumeric: 'tabular-nums',
                                        }}
                                    >
                                        <strong>{i + 1}.</strong> {humanDateShort(d)}
                                    </Box>
                                ))
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}
